import { FlashbarProps } from '@cloudscape-design/components';
import { useAtom } from 'jotai';
import { useCallback } from 'react';
import { flashbarAtom, flashbarClearCounterAtom } from '../../store/flashbarAtom';

/**
 * A custom hook for managing flashbar notifications in the application.
 * @see https://cloudscape.aws.dev/components/flashbar?tabId=api
 *
 * This hook provides:
 * - State management for flashbar messages
 * - Functions to add, remove, and clear flashbar messages
 * - using Cloudscape's Flashbar component.
 */
export const useFlashbar = () => {
  const [flashbarMessages, setFlashbarMessages] = useAtom(flashbarAtom);
  const [, setClearCounter] = useAtom(flashbarClearCounterAtom);
  const DEFAULT_TIMEOUT = 10000;

  const generateId = () => {
    return Math.floor(Math.random() * 100000);
  };

  const addFlashbarMessage = useCallback(
    (flashbar: FlashbarProps.MessageDefinition) => {
      setFlashbarMessages((current) => {
        // Don't add if message with same id already exists; reduces re-rendering
        if (flashbar.id && current.some((msg) => msg.id === flashbar.id)) {
          return current;
        }
        return [flashbar];
      });

      // Auto clear after timeout unless loading is true
      if (!flashbar.loading) {
        setTimeout(() => {
          // Only clear if this is still the current message
          setFlashbarMessages((currentMessages) => {
            if (currentMessages[0]?.content === flashbar.content) {
              return [];
            }
            return currentMessages;
          });
        }, DEFAULT_TIMEOUT);
      }
    },
    [setFlashbarMessages]
  );

  const clearFlashbarMessages = useCallback(() => {
    setFlashbarMessages([]);
    setClearCounter((c) => c + 1);
  }, [setFlashbarMessages, setClearCounter]);

  const addMessage = useCallback(
    (message: string, type: FlashbarProps.Type = 'info', loading: boolean = false) => {
      addFlashbarMessage({
        id: `flashbar-${generateId()}`,
        type,
        content: message,
        dismissible: true,
        loading,
        dismissLabel: 'Dismiss message',
        onDismiss: clearFlashbarMessages,
      });
    },
    [addFlashbarMessage, clearFlashbarMessages]
  );

  return {
    flashbarMessages,
    addFlashbarMessage,
    addMessage,
    clearFlashbarMessages,
  };
};
